import { z } from 'zod';

import { languageCodeSchema, languageCodeSchemaForJSA } from '../internationalization';
import { jobArchitectureProfileReviewStateSchema } from './job-profile';
import { simplePaginationSchema } from './pagination';
import { personSchema } from './person';

export const COMPANY_METADATA_TEXT_MAX_LENGTH = 100_000;

export const jobArchitectureOptionsResponseSchema = z.object({
    canCreate: z.boolean(),
    canRead: z.boolean(),
});

export type JobArchitectureOptionsResponse = z.infer<typeof jobArchitectureOptionsResponseSchema>;

export const jobArchitecturePermissionsSchema = jobArchitectureOptionsResponseSchema.transform((value) => ({
    canUserCreateJobArchitecture: value.canCreate,
    canUserGetJobArchitecture: value.canRead,
}));

export type JobArchitecturePermissions = z.infer<typeof jobArchitecturePermissionsSchema>;

export const jobArchitectureInflightSchema = z.object({
    clusterGenerations: z.number(),
    clusterTranslations: z.number(),
    familyGenerations: z.number(),
    familyTranslations: z.number(),
    hasTasksInFlight: z.boolean(),
    jobArchitectureId: z.string(),
    levelGenerations: z.number(),
    levelTranslations: z.number(),
    roleGenerations: z.number(),
    roleTranslations: z.number(),
    skillDetections: z.number(),
    totalInFlight: z.number(),
});

export type JobArchitectureInflight = z.infer<typeof jobArchitectureInflightSchema>;

export const jobArchitecturePathSchema = z.object({
    cluster: z.string().nullish(),
    family: z.string().nullish(),
    level: z.string().nullish(),
    role: z.string().nullish(),
});

export type JobArchitecturePath = z.infer<typeof jobArchitecturePathSchema>;

export const jobArchitectureOnboardingStateSchema = z
    .object({
        familiesAccepted: z.boolean().default(true),
        levelsAccepted: z.boolean().default(true),
        levelsHaveReviewersAccepted: z.boolean().default(false),
        levelsReviewedAccepted: z.boolean().default(false),
        metadataCompleted: z.boolean().default(true),
        onboardingCompleted: z.boolean().default(true),
        reviewersAssigned: z.boolean().default(true),
        rolesAccepted: z.boolean().default(true),
        rolesHaveReviewersAccepted: z.boolean().default(false),
        rolesReviewedAccepted: z.boolean().default(false),
    })
    .passthrough()
    .default({
        familiesAccepted: true,
        levelsAccepted: true,
        levelsHaveReviewersAccepted: false,
        levelsReviewedAccepted: false,
        metadataCompleted: true,
        onboardingCompleted: true,
        reviewersAssigned: true,
        rolesAccepted: true,
        rolesHaveReviewersAccepted: false,
        rolesReviewedAccepted: false,
    });

export type JobArchitectureOnboardingState = z.infer<typeof jobArchitectureOnboardingStateSchema>;

export const jobArchitectureItemStatisticsSchema = z.object({
    approvedLevels: z.number().int().nonnegative().default(0),
    approvedRoles: z.number().int().nonnegative().default(0),
    clustersCount: z.number().int().nonnegative().default(0),
    expected: z.number().int().nonnegative().default(0),
    familiesCount: z.number().int().nonnegative().default(0),
    levelsCount: z.number().int().nonnegative().default(0),
    profiles: z.number().int().nonnegative().default(0),
    publishedLevels: z.number().int().nonnegative().default(0),
    publishedRoles: z.number().int().nonnegative().default(0),
    reviewed: z.number().int().nonnegative().default(0),
    reviewerLevels: z.number().int().nonnegative().default(0),
    reviewerRoles: z.number().int().nonnegative().default(0),
    reviewers: z.number().int().nonnegative().default(0),
    rolesCount: z.number().int().nonnegative().default(0),
});

export type JobArchitectureItemStatistics = z.infer<typeof jobArchitectureItemStatisticsSchema>;

const detectionStateSchema = z.enum(['queued', 'started', 'finished', 'failed']).nullable().optional();

export const jobArchitectureGenerationInfoSchema = z.object({
    inProgress: z.boolean(),
    updatedAt: z.string().optional(),
    skillDetectionState: detectionStateSchema,
    translationState: detectionStateSchema,
});

export type JobArchitectureGenerationInfo = z.infer<typeof jobArchitectureGenerationInfoSchema>;

const jobArchitectureGenerationInfoWithDebugSchema = jobArchitectureGenerationInfoSchema.extend({
    createdAt: z.string(),
    debugInformation: z.record(z.string(), z.unknown().transform(String)).optional(),
    state: z.enum(['queued', 'started', 'finished', 'failed']),
    updatedAt: z.string(),
});

export const jobArchitectureDebugInfoSchema = z.object({
    generationInfo: jobArchitectureGenerationInfoWithDebugSchema.optional(),
    profileGenerationInfo: jobArchitectureGenerationInfoWithDebugSchema.optional(),
});

export type JobArchitectureDebugInformation = z.infer<typeof jobArchitectureDebugInfoSchema>;

export const companySizeNumberSchema = z.number().int().min(1).max(10_000_000);

export const primaryGoalTypeSchema = z.enum(['eu_pay_transparency', 'other', 'reorganization', 'sap_success_factors']);

export type PrimaryGoalType = z.infer<typeof primaryGoalTypeSchema>;

export const jobArchitectureMetadataSchema = z.object({
    businessUnits: z.string().array(),
    companyDescription: z.string(),
    companyExecutiveRolesInFamily: z.boolean(),
    companyName: z.string().nullable().optional(),
    companyPriorities: z
        .string()
        .nullable()
        .optional()
        .transform((value) => value ?? undefined),
    companySize: companySizeNumberSchema,
    expectedNumberOfProfiles: z.number().nonnegative().optional(),
    primaryGoalOtherText: z.string().nullable().optional(),
    primaryGoals: primaryGoalTypeSchema.array().default([]),
});

export type JobArchitectureMetadata = z.infer<typeof jobArchitectureMetadataSchema>;

export const nullableJobArchitectureMetadataSchema = z.preprocess(
    (val) => (val === '' ? null : val),
    jobArchitectureMetadataSchema.nullable(),
);

export const jobArchitectureStatusSchema = z.enum(['READY', 'REVIEW', 'DONE']);

export type JobArchitectureStatus = z.infer<typeof jobArchitectureStatusSchema>;

export const jobArchitectureSchema = z.object({
    id: z.string(),
    actions: z.object({ canEdit: z.boolean() }).optional(),
    childCount: z.number().int().nonnegative(),
    description: z.string().nullable().optional(),
    generationInfo: jobArchitectureGenerationInfoSchema.nullable().optional(),
    isMetadataCompleted: z.boolean().default(false),
    isPendingGeneration: z.boolean().default(false),
    metadata: jobArchitectureMetadataSchema.nullable().optional(),
    statistics: jobArchitectureItemStatisticsSchema,
    status: jobArchitectureStatusSchema.default('DONE'),
    title: z.string(),
    translations: z
        .object({
            description: z.string().optional(),
            language: languageCodeSchema,
            title: z.string(),
        })
        .array(),
});

export const jobArchitecturesSchema = jobArchitectureSchema.array();

export type JobArchitecture = z.infer<typeof jobArchitectureSchema>;

export type JobArchitectures = z.infer<typeof jobArchitecturesSchema>;

export const isJobArchitectureSetupComplete = (architecture: JobArchitecture): boolean =>
    architecture.isMetadataCompleted || architecture.statistics.familiesCount > 0 || architecture.childCount > 0;

const selectedItemStateSchema = z.union([z.literal('default'), z.literal('selected'), z.literal('selectedPath')]);

export const jobArchitectureItemSchema = z.object({
    id: z.string(),
    architectureId: z.string(),
    childCount: z.number().int().nonnegative().default(0),
    description: z.string().optional(),
    parentId: z.string().nullable(),
    selectedState: selectedItemStateSchema.default('default'),
    statistics: jobArchitectureItemStatisticsSchema.default({
        approvedLevels: 0,
        approvedRoles: 0,
        clustersCount: 0,
        expected: 0,
        familiesCount: 0,
        levelsCount: 0,
        profiles: 0,
        publishedLevels: 0,
        publishedRoles: 0,
        reviewed: 0,
        reviewerLevels: 0,
        reviewerRoles: 0,
        reviewers: 0,
        rolesCount: 0,
    }),
    title: z.string(),
    translations: z
        .object({
            description: z.string().optional(),
            language: languageCodeSchemaForJSA,
            title: z.string(),
        })
        .array(),
});

export type JobArchitectureItemType = 'job-cluster' | 'job-family' | 'job-level' | 'job-role';

type SelectedItemState = z.infer<typeof selectedItemStateSchema>;

export type JobArchitectureItem<T extends { readonly type: JobArchitectureItemType }> = T &
    z.infer<typeof jobArchitectureItemSchema> & { readonly selectedState: SelectedItemState };

const jobArchitectureSearchItemPathSchema = z.record(z.string(), z.uuid());

export const jobProfileReviewerSchema = z.object({
    id: z.uuid(),
    emailAddress: z.string().nullable().default(null),
    firstName: z.string(),
    lastName: z.string(),
    state: jobArchitectureProfileReviewStateSchema,
});

export const jobArchitectureSearchItemSchema = z.object({
    id: z.uuid(),
    childCount: z.number().int().nonnegative(),
    externalId: z.string().nullable(),
    level: z.enum(['family', 'cluster', 'role', 'level', 'root']),
    path: jobArchitectureSearchItemPathSchema,
    publishedAt: z.iso.datetime().nullish(),
    reviewer: jobProfileReviewerSchema.nullable(),
    state: z.enum(['draft', 'active', 'archived']).nullable(),
    statistics: jobArchitectureItemStatisticsSchema,
    title: z.string(),
});

export const jobArchitectureSearchResponseSchema = z.object({
    data: z.array(jobArchitectureSearchItemSchema),
    pagination: simplePaginationSchema,
});

export type JobProfileReviewer = z.infer<typeof jobProfileReviewerSchema>;
export type JobArchitectureSearchItem = z.infer<typeof jobArchitectureSearchItemSchema>;
export type JobArchitectureSearchResponse = z.infer<typeof jobArchitectureSearchResponseSchema>;

const reviewerFilterSchema = z.object({
    field: z.literal('reviewerId'),
    type: z.literal('user'),
    values: z.array(personSchema.nullable()),
});

export const jobArchitectureFiltersResponseSchema = z.object({
    filters: z.array(
        z.union([
            reviewerFilterSchema,
            z.object({
                field: z.string(),
                type: z.string(),
                values: z.array(z.unknown().nullable()).optional(),
            }),
        ]),
    ),
});

export type ReviewerFilter = z.infer<typeof reviewerFilterSchema>;
export type JobArchitectureFiltersResponse = z.infer<typeof jobArchitectureFiltersResponseSchema>;
